// Master Apps · textos de ajuda dos campos de enum do cadastro de aplicação.
//
// Formato consumido pelo InfoPopover (`@/components/info-popover`): as chaves
// title/subtitle/sections são estruturalmente compatíveis com as props dele,
// então dá para espalhar direto:
//   <InfoPopover {...FIELD_HELP.tipo} dark={dark} accent={accent} />
//
// Conteúdo em PT-BR, gênero masculino, sem travessão (usamos "·").

export interface FieldHelpSection {
  /** Cabeçalho da seção. */
  h: string;
  /** Parágrafo corrido. */
  b?: string;
  /** Itens com marcador. */
  list?: string[];
}

export interface FieldHelpEntry {
  title: string;
  subtitle?: string;
  sections: FieldHelpSection[];
}

export type FieldHelpKey = 'tipo' | 'visibilidade' | 'situacao' | 'criticidade';

export const FIELD_HELP: Record<FieldHelpKey, FieldHelpEntry> = {
  // enum: power-apps | fullstack | web | mobile | api | integracao | rpa | data | infra
  tipo: {
    title: 'Tipo de aplicação',
    subtitle: 'Como a aplicação foi construída',
    sections: [
      {
        h: 'Como escolher',
        b: 'O tipo descreve a tecnologia principal da aplicação, não o assunto que ela atende. Quando mais de uma opção parecer válida, escolha a que representa o maior esforço de desenvolvimento.',
      },
      {
        h: 'Opções disponíveis',
        list: [
          'Power Apps · app low-code da Power Platform (Power Fx, Dataverse, SharePoint)',
          'Fullstack · aplicação própria com frontend e backend desenvolvidos pela TI',
          'App web · site ou portal web sem backend próprio relevante',
          'Mobile · aplicativo para celular',
          'API · serviço backend consumido por outros sistemas',
          'Integração · fluxo que conecta sistemas (SAP, WMS, TOTVS, n8n)',
          'RPA · automação que simula ações do usuário',
          'Dados / BI · pipeline, painel ou modelo analítico',
          'Infra · ferramenta interna de infraestrutura (registry, proxy, monitoramento)',
        ],
      },
      {
        h: 'Dica',
        b: 'O tipo define quais requisitos de documentação são exigidos. Por exemplo, documentação de API só é cobrada de API, Integração e Fullstack.',
      },
    ],
  },

  // enum: publico | privado | interno
  visibilidade: {
    title: 'Visibilidade no catálogo',
    subtitle: 'Quem enxerga a aplicação',
    sections: [
      {
        h: 'Opções disponíveis',
        list: [
          'Público · visível a todos os colaboradores no catálogo',
          'Privado · aparece no catálogo apenas para quem tem acesso concedido ou login, não é divulgado abertamente',
          'Interno · infraestrutura e ferramentas de TI, só os administradores enxergam (não aparece no catálogo do colaborador)',
        ],
      },
      {
        h: 'Impacto na auditoria',
        b: 'Aplicações internas são dispensadas de alguns requisitos da auditoria: regra de negócio, requisitos, repositório e chamado de origem.',
      },
    ],
  },

  // enum: prod | homolog | dev | manutencao | pausado | depreciado
  situacao: {
    title: 'Situação da aplicação',
    subtitle: 'Estágio do ciclo de vida',
    sections: [
      {
        h: 'Opções disponíveis',
        list: [
          'Produção · em uso pelos colaboradores',
          'Homologação · em validação, os dados podem ser de teste',
          'Desenvolvimento · em construção, sem uso oficial',
          'Manutenção · em produção, mas com intervenção em andamento ou instabilidade conhecida',
          'Pausado · começou e parou por decisão; não foi descontinuado e pode ser retomado',
          'Depreciado · substituído ou em processo de desativação, mantido só por histórico',
        ],
      },
      {
        h: 'Pausado ou Depreciado?',
        b: 'Pausado é reversível: o projeto está congelado à espera de prioridade. Depreciado é definitivo: já foi substituído ou desligado. Na dúvida, pergunte se alguém ainda pretende retomar; se a resposta for sim, é Pausado.',
      },
      {
        h: 'Onde a aplicação aparece',
        list: [
          'Aba Produção · mostra as situações Produção, Manutenção e Depreciado',
          'Aba Homologação · mostra os apps com URL de homologação ou com situação Homologação',
          'Aba Pausado · só para administradores, com os projetos congelados',
        ],
      },
    ],
  },

  // enum: alta | media | baixa
  criticidade: {
    title: 'Criticidade para o negócio',
    subtitle: 'Impacto se a aplicação parar',
    sections: [
      {
        h: 'Critério',
        b: 'A criticidade mede o impacto no negócio se a aplicação parar, não a complexidade técnica nem a quantidade de usuários.',
      },
      {
        h: 'Níveis',
        list: [
          'Alta · para produção, faturamento, expedição ou obrigação legal; a indisponibilidade gera prejuízo imediato',
          'Média · impacta um setor, mas existe contorno manual',
          'Baixa · uso pontual ou de apoio, sem impacto operacional direto',
        ],
      },
      {
        h: 'Como a auditoria usa',
        b: 'Aplicações de criticidade alta são priorizadas no plano de remediação e exigem plano de contingência documentado.',
      },
    ],
  },
};
